<template>
  <div class="trash-view">
    <AppHeader />

    <div class="content-wrapper">
      <div class="trash-header">
        <h1><IconSvg name="trash-solid-full" size="lg" /> ゴミ箱</h1>
        <p class="description">削除したしおりは30日後に完全に削除されます</p>
      </div>

      <div v-if="loading" class="loading">読み込み中...</div>

      <div v-else-if="trashedShiori.length === 0" class="empty-state">
        <p><IconSvg name="check-solid-full" size="md" /> ゴミ箱は空です</p>
      </div>

      <div v-else class="trash-grid">
        <div
          v-for="item in trashedShiori"
          :key="item.SHIORI_NOV"
          class="trash-card"
        >
          <div class="card-content">
            <h3>{{ item.SUBJECT_NMV }}</h3>
            <p class="date-range">
              {{ formatDate(item.TRIP_FROM) }} 〜 {{ formatDate(item.TRIP_TO) }}
            </p>
            <p class="deleted-date">
              削除日: {{ formatDate(item.UPDATE_YMD) }}
            </p>
          </div>

          <div class="card-actions">
            <button @click="handleRestore(item)" class="btn btn-restore">
              <IconSvg name="recycle-solid-full" size="sm" /> 復元
            </button>
            <button @click="handlePermanentDelete(item)" class="btn btn-delete">
              <IconSvg name="trash-solid-full" size="sm" /> 完全削除
            </button>
          </div>
        </div>
      </div>

      <button @click="goBack" class="btn-back">← マイページに戻る</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useShioriStore } from "../store/shiori-store";
import AppHeader from "../components/AppHeader.vue";
import IconSvg from "../components/IconSvg.vue";

export default {
  name: "TrashView",
  components: {
    AppHeader,
    IconSvg,
  },
  setup() {
    const router = useRouter();
    const shioriStore = useShioriStore();
    const trashedShiori = ref([]);
    const loading = ref(true);

    const fetchTrashedShiori = async () => {
      loading.value = true;
      try {
        trashedShiori.value = await shioriStore.fetchTrashedShiori();
      } catch (error) {
        console.error("ゴミ箱データ取得エラー:", error);
        alert("ゴミ箱データの取得に失敗しました");
      } finally {
        loading.value = false;
      }
    };

    const handleRestore = async (item) => {
      if (confirm(`「${item.SUBJECT_NMV}」を復元しますか？`)) {
        try {
          await shioriStore.restoreShiori(item.SHIORI_NOV);
          alert("しおりを復元しました");
          fetchTrashedShiori();
        } catch (error) {
          console.error("復元エラー:", error);
          alert("復元に失敗しました");
        }
      }
    };

    const handlePermanentDelete = async (item) => {
      if (
        confirm(
          `「${item.SUBJECT_NMV}」を完全に削除しますか？この操作は取り消せません。`,
        )
      ) {
        try {
          await shioriStore.permanentDeleteShiori(item.SHIORI_NOV);
          alert("しおりを完全に削除しました");
          fetchTrashedShiori();
        } catch (error) {
          console.error("完全削除エラー:", error);
          alert("削除に失敗しました");
        }
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return `${date.getFullYear()}年${
        date.getMonth() + 1
      }月${date.getDate()}日`;
    };

    const goBack = () => {
      router.push("/mypage");
    };

    onMounted(() => {
      fetchTrashedShiori();
    });

    return {
      trashedShiori,
      loading,
      handleRestore,
      handlePermanentDelete,
      formatDate,
      goBack,
    };
  },
};
</script>

<style scoped>
.trash-view {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem 2rem;
}

.trash-header {
  text-align: center;
  margin-bottom: 3rem;
}

.trash-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.description {
  color: #666;
  font-size: 0.95rem;
}

.loading {
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
}

.empty-state {
  text-align: center;
  padding: 5rem 2rem;
  font-size: 1.5rem;
  color: #999;
}

.trash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.trash-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
}

.trash-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.card-content h3 {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 0.75rem;
}

.date-range {
  color: #666;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.deleted-date {
  color: #999;
  font-size: 0.85rem;
  font-style: italic;
  margin-bottom: 1rem;
}

.card-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-restore {
  background-color: var(--primary-color, #4caf50);
  color: white;
}

.btn-restore:hover {
  background-color: #45a049;
  transform: scale(1.05);
}

.btn-delete {
  background-color: #f44336;
  color: white;
}

.btn-delete:hover {
  background-color: #d32f2f;
  transform: scale(1.05);
}

.btn-back {
  display: block;
  margin: 2rem auto;
  padding: 0.75rem 2rem;
  background-color: #666;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back:hover {
  background-color: #555;
  transform: translateY(-2px);
}
</style>
